import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import fileDownload from 'js-file-download';
import PropTypes from 'prop-types';
import {
  getScriptAttachmentsRequest,
  scriptNoteOrAttachmentIds,
  uploadAttachmentFileRequest,
} from '../../../../../store/actions/script';
import accountHelpers from '../../../../../utils/accountHelpers';
import { sendInsuranceFileRequest } from '../../../../../store/actions/patients';
import AttachedFile from '../../../../_common/AttachedFile/AttachedFile';
import { setLoadingProgress } from '../../../../../store/actions/loadingProgess';
import api from '../../../../../api/api';
import { PatientAndScriptTabHeader } from '../../../../../utils/data/data';

const typeOptions = ['Rx',
  'Delivery Signature',
  'PA form',
  'Medical Notes',
  'Copay Assistance Form',
  'Income Documents',
  'Shipping Label',
  'Other'];

let firstLoad = true;

const Attachments = ({ changeCount, type }) => {
  const dispatch = useDispatch();
  const { scriptAttachment, requestStatus, attachmentsId } = useSelector((store) => store.script);
  const { loadProgress } = useSelector((store) => store.loadingProgress);
  const { scriptId } = useParams();

  const historyTabRef = document.getElementById('rx-history-id');

  const [sendFile, setSendFile] = useState({
    fileName: '',
    attachmentFile: null,
    type: '',
    scriptId,
    userId: accountHelpers.getUser().id,
    attachedBy: accountHelpers.getUser().username,
  });

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [btnLoad, setBtnLoad] = useState(false);

  const allScriptNoteIds = scriptAttachment.map((s) => s.attachmentId);
  const isCheckedAllAttachedIds = allScriptNoteIds.every((id) => attachmentsId.includes(id));

  useEffect(() => {
    (async () => {
      firstLoad = true;

      await dispatch(getScriptAttachmentsRequest({
        userId: accountHelpers.getUser().id,
        ScriptId: scriptId,
        type,
      }));
      firstLoad = false;
    })();

    dispatch(scriptNoteOrAttachmentIds({ name: 'attachment', ids: [] }));
  }, [type]);

  const onFileClick = async (attachmentId, fileName) => {
    if (loadProgress.status) {
      toast.error('please with finished download file');
      return;
    }

    try {
      dispatch(setLoadingProgress({
        status: 'Download start',
        percent: 0,
      }));

      const { data } = await api.getFile(
        {
          scriptId, userId: accountHelpers.getUser().id, attachmentId, fileName,
        },
        ({ total, loaded }) => {
          const percent = Math.round((loaded * 100) / total);

          dispatch(setLoadingProgress({
            status: 'Loading',
            percent,
          }));
        },
      );

      fileDownload(new Uint8Array(data.Body.data), fileName);

      toast.success('Download completed');
      dispatch(setLoadingProgress({
        status: 'Download completed',
        percent: 0,
      }));

      const timeout = setTimeout(() => {
        dispatch(setLoadingProgress({
          status: '',
          percent: 0,
        }));

        clearTimeout(timeout);
      }, 1000);
    } catch (e) {
      console.warn(e);

      dispatch(setLoadingProgress({
        status: '',
        percent: 0,
      }));

      toast.error(`Unable to retrieving ${fileName}`);
    }
  };

  const onChangeFile = (file) => {
    setSendFile((prev) => ({ ...prev, attachmentFile: file, fileName: file.name }));
  };

  const onCloseModal = () => {
    setSendFile({
      fileName: '',
      attachmentFile: null,
      type: '',
      scriptId,
      userId: accountHelpers.getUser().id,
      attachedBy: accountHelpers.getUser().username,
    });

    setIsOpenModal(false);
    setBtnLoad(false);
  };

  const onSaveFile = async () => {
    if (!sendFile.attachmentFile || !sendFile.type || requestStatus === 'request') {
      if (!sendFile.attachmentFile && !sendFile.type) {
        toast.error('Please select file and type');
      } else if (!sendFile.attachmentFile) {
        toast.error('Please select file');
      } else if (!sendFile.type) {
        toast.error('Please select type');
      }

      return;
    }

    setBtnLoad(true);

    const data = await dispatch(uploadAttachmentFileRequest(sendFile));

    if (data.status === 'ok') {
      const { status } = await dispatch(sendInsuranceFileRequest({
        fileName: sendFile.fileName,
        attachmentId: data.payload.data.data.attachmentId,
        attachedBy: sendFile.attachedBy,
        attachmentFile: sendFile.attachmentFile,
      }));

      if (status === 'ok') {
        toast.success('File uploaded');

        await dispatch(getScriptAttachmentsRequest({
          userId: accountHelpers.getUser().id,
          ScriptId: scriptId,
        }));

        setBtnLoad(false);
        changeCount?.('Attachments');

        onCloseModal();
      } else {
        toast.error('File uploaded failed');

        setBtnLoad(false);
      }
    } else {
      toast.error('File uploaded failed');

      setBtnLoad(false);
    }
  };

  const onCheckBoxChange = (ids) => {
    if (ids === 'all') {
      if (isCheckedAllAttachedIds) {
        dispatch(scriptNoteOrAttachmentIds({ name: 'attachment', ids: [] }));
      } else {
        dispatch(scriptNoteOrAttachmentIds({ name: 'attachment', ids: allScriptNoteIds }));
      }
    } else if (attachmentsId.some((s) => s === ids)) {
      dispatch(scriptNoteOrAttachmentIds({
        name: 'attachment',
        ids: attachmentsId.filter((s) => s !== ids),
      }));
    } else {
      dispatch(scriptNoteOrAttachmentIds({ name: 'attachment', ids: [...attachmentsId, ids] }));
    }
  };

  return (
    <AttachedFile
      sendFile={sendFile}
      onChangeFile={onChangeFile}
      onCloseModal={onCloseModal}
      onSaveFile={onSaveFile}
      isOpenModal={isOpenModal}
      requestStatus={requestStatus}
      firstLoad={firstLoad}
      attachments={scriptAttachment}
      header={PatientAndScriptTabHeader}
      setIsOpenModal={setIsOpenModal}
      onFileClick={onFileClick}
      typeOptions={typeOptions}
      setSendFile={setSendFile}
      onCheckBoxChange={onCheckBoxChange}
      isCheckedAllAttachedIds={isCheckedAllAttachedIds}
      scriptAttachedIds={attachmentsId}
      historyTabRef={historyTabRef}
      btnLoad={btnLoad}
      type="script"
      hideActionButtons={!!type}
    />
  );
};

Attachments.prototype = {
  changeCount: PropTypes.func.isRequired,
};

export default Attachments;
